import { measure } from '@/components/Analytics/Analytics';
import ExpandableImage, {
	ExpandButton,
} from '@/components/ExpandableImage/ExpandableImage';
import ArrowLeftIcon from '@/components/Icon/ArrowLeftIcon';
import CameraIcon from '@/components/Icon/CameraIcon';
import ExpertIcon from '@/components/Icon/ExpertIcon';
import RecorderIcon from '@/components/Icon/RecorderIcon';
import IconButton from '@/components/IconButton/IconButton';
import LabelBackground from '@/components/LabelBackground/LabelBackground';
import LargeSquareBackground from '@/components/LargeSquareBackground/LargeSquareBackground';
import SearchButton from '@/components/Search/SearchButton';
import SeasonIllustrationBackground from '@/components/SeasonIllustrationBackground/SeasonIllustrationBackground';
import useCalendar from '@/hooks/calendar/useCalendar';
import useCards, { useCardMainMedia } from '@/hooks/cards/useCards';
import useSpecies from '@/hooks/species/useSpecies';
import useIsOnline from '@/hooks/useIsOnline';
import useUrlHistorySyncedOpenState from '@/hooks/useUrlHistorySyncedOpenState';
import cn from '@/lib/cs';
import { motion, useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ManualUpload from './ManualUpload';
import SpeciesDetails from './SpeciesDetails';
import SpotNowButton from './SpotNowButton';
import UserMediaGallery from './UserMediaGallery';
import Vlog from './Vlog';

const SpeciesRoute = () => {
	const navigate = useNavigate();
	const params = useParams();
	const speciesParams = useMemo(
		() => ({
			speciesId: Number((params['id'] ?? '').split('-')[0]),
			speciesSlug: (params['id'] ?? '').split('-').slice(1).join('-'),
		}),
		[params],
	);
	const {
		species: allSpecies,
		findSpeciesById,
		getMainImageById,
	} = useSpecies();

	// Check if the species is in the calendar, to hide certain information
	const { getIsSpeciesInCalendar } = useCalendar();
	const isSpeciesInCalendar = useMemo(
		() => getIsSpeciesInCalendar(speciesParams.speciesId),
		[getIsSpeciesInCalendar, speciesParams],
	);

	// Reduce motion or not
	const reduceMotion = useReducedMotion();

	// Show gallery or not
	const [showUserPhotoGallery, setShowUserPhotoGallery] =
		useUrlHistorySyncedOpenState();

	// Scroll to top if location or params change
	const navRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (navRef.current)
			navRef.current.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			});
	}, [speciesParams.speciesId, navRef]);

	// Get the species info
	const species = useMemo(() => {
		if (allSpecies) return findSpeciesById(speciesParams.speciesId);
		else return undefined;
	}, [allSpecies, findSpeciesById, speciesParams]);

	// Get the species photo if it exists and refresh if location or params change
	const { getCardBySpeciesId } = useCards();
	const card = useMemo(
		() => getCardBySpeciesId(speciesParams.speciesId),
		[getCardBySpeciesId, speciesParams],
	);
	const { mainUserPhoto, isUserAudioAvailable } = useCardMainMedia(
		species?.id ?? speciesParams.speciesId,
		['photo', 'audio'],
		true,
	);

	// Check if audio recognition is supported for this species
	const { getSpeciesSupportsAudioRecognition } = useSpecies();
	const isRecorderEnabled = useMemo(
		() => getSpeciesSupportsAudioRecognition(species?.id ?? 0),
		[getSpeciesSupportsAudioRecognition, species],
	);

	// If the user is offline, show an error
	const isOnline = useIsOnline();
	const onlyRunIfOnline = useCallback(
		(func: () => void) => {
			if (
				isOnline ||
				confirm(
					"Ben je online? De camera en recorder kunnen je foto's en geluiden herkennen als je online bent.",
				)
			)
				func();
		},
		[isOnline],
	);

	// Open recorder
	const openRecorder = useCallback(() => {
		if (!species) return;
		onlyRunIfOnline(() => {
			measure(
				`species-spotbutton-${species.id}-${species.attributes.slug}-recorder`,
				'navigation',
			);
			navigate('/recorder', {
				state: {
					speciesId: species.id,
				},
			});
		});
	}, [navigate, onlyRunIfOnline, species]);

	// Open camera
	const openCamera = useCallback(() => {
		if (!species) return;
		onlyRunIfOnline(() => {
			measure(
				`species-spotbutton-${species.id}-${species.attributes.slug}-camera`,
				'navigation',
			);
			navigate('/camera', {
				state: {
					speciesId: species.id,
				},
			});
		});
	}, [navigate, onlyRunIfOnline, species]);

	// If the species is not found, return null
	if (species === undefined) return null;

	// Get image
	const mainImage = getMainImageById(species.id);

	// Is expert species?
	const isExpertSpecies = species?.attributes.level === 'expert';

	return (
		<SeasonIllustrationBackground>
			<motion.main
				key={species.id}
				initial={{
					opacity: 0,
					scale: reduceMotion ? 1 : 1.25,
				}}
				animate={{
					opacity: 1,
					scale: 1,
					transition: {
						ease: 'circOut',
					},
				}}
				className="fixed inset-0 overflow-y-auto overflow-x-hidden bg-gray-950/20 pb-32">
				<nav
					ref={navRef}
					className="z-20 flex flex-col items-center justify-center py-4">
					<IconButton
						className="fixed start-2 top-2 z-10 lg:start-4 lg:top-4"
						animationDelay={0.3}
						onClick={() => {
							measure(
								`species-back-${species.id}-${species.attributes.slug}`,
								'exit',
							);
							navigate(-1);
						}}
						aria-label="Terug"
						title="Terug"
						icon={<ArrowLeftIcon />}
					/>
					<h1 className="relative px-4 pb-1 pt-2 font-brand text-2xl text-accentCompat md:text-3xl lg:text-4xl">
						<LabelBackground className="text-accent" />
						<span className="relative z-10">
							{species?.attributes.titleLong}
						</span>
					</h1>
					<SearchButton
						isSearchComponentMissing
						searchResultTarget="details"
						onClickMeasure={() =>
							measure(
								`species-search-${species.id}-${species.attributes.slug}`,
								'navigation',
							)
						}
					/>
				</nav>
				<div className="items-center justify-center gap-4 sm:flex">
					<motion.div
						className="flex shrink-0 flex-col items-center justify-center pb-4"
						initial={{
							scale: reduceMotion ? 1 : 0.7,
						}}
						animate={{
							scale: 1,
							transition: {
								delay: 0.1,
							},
						}}>
						{/* Camera button or user photo */}
						<motion.div
							layoutId={
								card ? `UserMedia-${card.id}-${0}` : undefined
							}
							layoutDependency={showUserPhotoGallery}
							className="relative ms-6 flex size-64 flex-col items-center justify-center gap-1 rounded-lg text-accentCompat"
							tabIndex={-1}
							onClick={() => {
								if (card) {
									setShowUserPhotoGallery(true);
									measure(
										`species-gallery-open-${species.id}-${species.attributes.slug}`,
										'navigation',
									);
								}
							}}
							initial={{
								rotate: 2,
							}}
							whileTap={{
								scale: 0.95,
							}}>
							{/* If the user has collected the species, show the user photo */}
							<LargeSquareBackground
								className="text-accent"
								image={
									mainUserPhoto
										? {
												base64: mainUserPhoto,
												alt: 'Laatste foto uit jouw verzameling',
											}
										: undefined
								}
							/>

							{/* If a recording is available, show recording icon */}
							{isUserAudioAvailable && !mainUserPhoto && (
								<div
									className="absolute inset-0 flex size-full items-center justify-center"
									role="img"
									aria-label="Jouw opname"
									title="Jouw opname">
									<RecorderIcon className="size-32 text-accentCompat opacity-75 lg:size-32" />
								</div>
							)}

							{/* Show spot now button if no media yet or show actions on top of the user media */}
							{!card ? (
								<SpotNowButton
									species={species}
									openCamera={openCamera}
									openRecorder={openRecorder}
								/>
							) : (
								<>
									<IconButton
										icon={
											<CameraIcon className="size-10 lg:size-10" />
										}
										aria-label="Camera starten en spotten"
										title="Camera starten en spotten"
										className="absolute start-4 top-6 z-50"
										onClick={(event) => {
											event.stopPropagation();
											openCamera();
										}}
									/>
									{isRecorderEnabled && (
										<IconButton
											icon={
												<RecorderIcon className="size-8 lg:size-8" />
											}
											aria-label="Microfoon starten en spotten"
											title="Microfoon starten en spotten"
											className="absolute start-24 top-2 z-50"
											onClick={(event) => {
												event.stopPropagation();
												openRecorder();
											}}
										/>
									)}
									<ExpandButton
										className="bottom-8 end-4"
										onClick={(event) => {
											event.stopPropagation();
											setShowUserPhotoGallery(true);
										}}
									/>
								</>
							)}
						</motion.div>
						{/* Main image from species */}
						{!!mainImage?.url && isSpeciesInCalendar && (
							<>
								<ExpandableImage
									src={mainImage.url}
									thumbnailSrc={mainImage.thumbnailUrl}
									alt={mainImage.alt}
									caption={mainImage.caption}
									className="-mt-8"
									imageClassName="object-contain scale-75"
									initial={{
										x: '-1.5rem',
									}}
									animate={{
										scale:
											showUserPhotoGallery &&
											!reduceMotion
												? 0
												: 1,
										transition: {
											delay:
												showUserPhotoGallery &&
												!reduceMotion
													? 0
													: 0.3,
										},
									}}>
									{/* Expert badge, position is based on if the user has collected the species */}
									{isExpertSpecies && (
										<motion.div
											initial={{
												opacity: 0,
												scale: 1.5,
												rotate: -6,
											}}
											animate={{
												opacity: 1,
												scale: [1.5, 1, 1.1, 1],
												transition: {
													delay: reduceMotion
														? 0
														: 0.5,
													duration: reduceMotion
														? 0.05
														: undefined,
												},
											}}
											className={cn(
												'absolute z-50',
												mainUserPhoto
													? '-start-2 -top-12'
													: '-start-6 -top-1',
											)}>
											<p className="relative -z-10 p-2 font-brand text-2xl text-accentCompat">
												<LabelBackground className="text-accent" />
												<span className="relative z-10 flex flex-col items-center justify-center">
													<ExpertIcon />
													Expert
												</span>
											</p>
										</motion.div>
									)}
								</ExpandableImage>
								{/* Expert description, if user has not collected the species yet */}
								{isExpertSpecies && !card && (
									<p className="relative -z-10 mx-auto -mt-12 w-min min-w-56 -translate-x-6 text-balance px-2 pb-4 pt-14 text-center font-sans text-lg text-accentCompat">
										<LabelBackground className="text-accent" />
										<span className="relative z-10">
											Voor echte experts! Deze soort is
											moeilijk om te verzamelen.
										</span>
									</p>
								)}
							</>
						)}
					</motion.div>

					{/* Species vlog video */}
					<Vlog species={species} />
				</div>

				{/* Species details */}
				<SpeciesDetails
					species={species}
					isSpeciesInCalendar={isSpeciesInCalendar}
				/>

				{/* Manual upload tool */}
				<ManualUpload speciesId={species.id} />

				{showUserPhotoGallery && (
					<UserMediaGallery
						species={species}
						mainUserPhoto={mainUserPhoto}
						onClose={() => {
							measure(
								`species-gallery-close-${species.id}-${species.attributes.slug}`,
								'exit',
							);
							setShowUserPhotoGallery(false);
						}}
					/>
				)}
			</motion.main>
		</SeasonIllustrationBackground>
	);
};

export default SpeciesRoute;
